/**
 * SupportPage
 *
 * Support hub for getting help, submitting tickets, and accessing resources.
 *
 * @module help
 * @spec 102-screen-completeness
 * @story US4 - Support Screens
 * @requirement FR-024
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LifeBuoy,
  MessageCircle,
  Phone,
  Mail,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronRight,
  ExternalLink,
  Send,
  FileText,
  BookOpen,
} from 'lucide-react';
import { ComingSoonBanner } from '../../components/ui/ComingSoonBanner';
import { supportHelp } from '../../data/help/supportHelp';
import { PageLayout, Card, Modal, Button, PageHelpPanel, AppTooltip } from '../../components/ui';

export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface SupportTicket {
  id: string;
  title: string;
  status: TicketStatus;
  priority: TicketPriority;
  createdAt: string;
  updatedAt: string;
  assignee?: string;
}

export interface SupportContact {
  name: string;
  role: string;
  email: string;
  phone?: string;
  availability: string;
}

export interface SupportPageProps {
  /** Recent tickets - undefined triggers coming soon state */
  tickets?: SupportTicket[];
  /** Support contacts */
  contacts?: SupportContact[];
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: Error | null;
  /** Callback for creating a new ticket */
  onCreateTicket?: (title: string, description: string) => void;
}

const STATUS_STYLES: Record<TicketStatus, { icon: React.FC<{ className?: string }>; color: string }> = {
  open: { icon: AlertCircle, color: 'text-blue-500' },
  in_progress: { icon: Loader2, color: 'text-yellow-500' },
  resolved: { icon: CheckCircle, color: 'text-green-500' },
  closed: { icon: XCircle, color: 'text-[var(--color-text-secondary)]' },
};

const PRIORITY_STYLES: Record<TicketPriority, { bg: string; text: string }> = {
  low: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-300' },
  medium: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
  high: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
  urgent: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
};

const QUICK_LINKS = [
  { icon: BookOpen, label: 'Documentation', path: '/help/docs', description: 'Browse guides and tutorials' },
  { icon: FileText, label: 'Runbooks', path: '/help/runbooks', description: 'Operational procedures' },
  { icon: ExternalLink, label: 'Status Page', path: '/monitoring/status', description: 'System status and uptime' },
];

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-6">
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-32 rounded-lg bg-[var(--color-bg-subtle)]" />
      ))}
    </div>
    <div className="h-64 rounded-lg bg-[var(--color-bg-subtle)]" />
  </div>
);

const TicketRow: React.FC<{ ticket: SupportTicket }> = ({ ticket }) => {
  const StatusIcon = STATUS_STYLES[ticket.status].icon;
  const statusColor = STATUS_STYLES[ticket.status].color;
  const priorityStyle = PRIORITY_STYLES[ticket.priority];

  return (
    <Link
      to={`/help/support/tickets/${ticket.id}`}
      className="flex items-center justify-between rounded-lg bg-[var(--color-bg-subtle)] p-4 transition-colors hover:bg-[var(--color-bg-subtle)]"
    >
      <div className="flex items-center gap-4">
        <StatusIcon className={`h-5 w-5 ${statusColor}`} />
        <div>
          <p className="font-medium text-[var(--color-text-primary)]">{ticket.title}</p>
          <p className="text-sm text-[var(--color-text-secondary)]">
            #{ticket.id} • Updated {ticket.updatedAt}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`rounded-full px-2 py-1 text-xs font-medium uppercase ${priorityStyle.bg} ${priorityStyle.text}`}>
          {ticket.priority}
        </span>
        <ChevronRight className="h-5 w-5 text-[var(--color-text-secondary)]" />
      </div>
    </Link>
  );
};

export const SupportPage: React.FC<SupportPageProps> = ({
  tickets,
  contacts,
  isLoading = false,
  error,
  onCreateTicket,
}) => {
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');

  const handleSubmitTicket = () => {
    if (ticketTitle && ticketDescription && onCreateTicket) {
      onCreateTicket(ticketTitle, ticketDescription);
      setShowTicketForm(false);
      setTicketTitle('');
      setTicketDescription('');
    }
  };

  // Show coming soon if no data
  if (!tickets && !isLoading && !error) {
    return (
      <PageLayout title="Support" icon={<LifeBuoy className="h-6 w-6" />}>
        <ComingSoonBanner
          title="Support Hub"
          description="Get help with the Fabric Control Plane. Submit support tickets, contact administrators, and access self-service resources."
          expectedAvailability="Phase 4 Implementation"
          relatedLinks={[
            { label: 'Documentation', path: '/help/docs' },
            { label: 'Runbooks', path: '/help/runbooks' },
          ]}
        />

        {/* Quick Links */}
        <div className="mt-8">
          <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
            Self-Service Resources
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {QUICK_LINKS.map((link) => (
              <Link key={link.path} to={link.path} className="block">
                <Card isHoverable className="flex items-center gap-4">
                  <link.icon className="h-8 w-8 text-[var(--color-accent)]" />
                  <div>
                    <p className="font-medium text-[var(--color-text-primary)]">{link.label}</p>
                    <p className="text-sm text-[var(--color-text-secondary)]">{link.description}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-8">
          <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
            Contact Support
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Card className="flex items-center gap-4">
              <Mail className="h-6 w-6 text-[var(--color-accent)]" />
              <div>
                <p className="font-medium text-[var(--color-text-primary)]">Email Support</p>
                <p className="text-sm text-[var(--color-text-secondary)]">support@fabric-control-plane.com</p>
              </div>
            </Card>
            <Card className="flex items-center gap-4">
              <Clock className="h-6 w-6 text-[var(--color-accent)]" />
              <div>
                <p className="font-medium text-[var(--color-text-primary)]">Response Time</p>
                <p className="text-sm text-[var(--color-text-secondary)]">24-48 hours for standard issues</p>
              </div>
            </Card>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="Support" icon={<LifeBuoy className="h-6 w-6" />}>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <h2 className="text-lg font-medium text-red-800 dark:text-red-200">
              Failed to load support data
            </h2>
          </div>
          <p className="mt-2 text-sm text-red-700 dark:text-red-300">
            {error.message}
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Support"
      description="Get help and manage support tickets"
      icon={<LifeBuoy className="h-6 w-6" />}
      actions={
        <>
          <PageHelpPanel content={supportHelp} buttonSize="sm" />
          <AppTooltip title="Open a new support ticket for assistance">
            <Button onClick={() => setShowTicketForm(true)} leftIcon={<MessageCircle className="h-4 w-4" />}>
              New Ticket
            </Button>
          </AppTooltip>
        </>
      }
    >
      <div data-testid="support-page">
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Quick Links */}
            <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
              {QUICK_LINKS.map((link) => (
                <Link key={link.path} to={link.path} className="block">
                  <Card isHoverable className="flex items-center gap-4">
                    <link.icon className="h-8 w-8 text-[var(--color-accent)]" />
                    <div>
                      <p className="font-medium text-[var(--color-text-primary)]">{link.label}</p>
                      <p className="text-sm text-[var(--color-text-secondary)]">{link.description}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Recent Tickets */}
            <Card className="mb-8">
              <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
                Recent Tickets
              </h2>
              {tickets && tickets.length > 0 ? (
                <div className="space-y-3">
                  {tickets.map((ticket) => (
                    <TicketRow key={ticket.id} ticket={ticket} />
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-[var(--color-text-secondary)]">
                  <MessageCircle className="mx-auto mb-4 h-12 w-12 opacity-50" />
                  <p className="text-lg font-medium">No tickets yet</p>
                  <p className="mt-1 text-sm">Submit a ticket to get help from support</p>
                </div>
              )}
            </Card>

            {/* Support Contacts */}
            {contacts && contacts.length > 0 && (
              <Card>
                <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
                  Support Contacts
                </h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {contacts.map((contact) => (
                    <div
                      key={contact.email}
                      className="flex items-start gap-4 rounded-lg bg-[var(--color-bg-subtle)] p-4"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-bg-subtle)]">
                        <span className="font-medium text-[var(--color-accent)]">
                          {contact.name.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[var(--color-text-primary)]">{contact.name}</p>
                        <p className="text-sm text-[var(--color-text-secondary)]">{contact.role}</p>
                        <div className="mt-2 space-y-1">
                          <a
                            href={`mailto:${contact.email}`}
                            className="flex items-center gap-1 text-sm text-[var(--color-accent)] hover:underline"
                          >
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </a>
                          {contact.phone && (
                            <a
                              href={`tel:${contact.phone}`}
                              className="flex items-center gap-1 text-sm text-[var(--color-accent)] hover:underline"
                            >
                              <Phone className="h-3 w-3" />
                              {contact.phone}
                            </a>
                          )}
                        </div>
                        <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
                          <Clock className="mr-1 inline h-3 w-3" />
                          {contact.availability}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* New Ticket Modal */}
            <Modal
              isOpen={showTicketForm}
              onClose={() => setShowTicketForm(false)}
              title="Create Support Ticket"
              size="sm"
              footer={
                <>
                  <Button variant="outline" onClick={() => setShowTicketForm(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSubmitTicket} disabled={!ticketTitle || !ticketDescription}>
                    <Send className="h-4 w-4" />
                    Submit Ticket
                  </Button>
                </>
              }
            >
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">
                    Title
                  </label>
                  <input
                    type="text"
                    value={ticketTitle}
                    onChange={(e) => setTicketTitle(e.target.value)}
                    placeholder="Brief description of the issue"
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 py-2 text-[var(--color-text-primary)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">
                    Description
                  </label>
                  <textarea
                    value={ticketDescription}
                    onChange={(e) => setTicketDescription(e.target.value)}
                    placeholder="Provide details about your issue..."
                    rows={4}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 py-2 text-[var(--color-text-primary)]"
                  />
                </div>
              </div>
            </Modal>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default SupportPage;
